import { useCallback, useEffect, useRef, useState } from "react";
import { captureDatadogRumAction } from "../../../telemetry/datadog-rum";
import {
  freshReplayTiming,
  durationMs,
  type ReplayIdentity,
  type ReplayTiming,
  type ReplayOutcome,
} from "./replay-timing";
import { replayTimingContext } from "./replay-timing-context";

export function useReplayTiming({
  coworldId,
  telemetryContext,
  surface,
}: ReplayIdentity) {
  const timing = useRef(
    freshReplayTiming({
      requestStartedAt: 0,
      hiddenSince: null,
      sessionShared: false,
    }),
  );
  const terminalCaptured = useRef(false);
  const [badge, setBadge] = useState<{
    delivery: ReplayTiming["delivery"];
    firstPaintMs?: number | undefined;
  } | null>(null);
  const readTiming = useCallback(
    (): Readonly<ReplayTiming> => timing.current,
    [],
  );
  const recordTiming = useCallback((update: Partial<ReplayTiming>) => {
    Object.assign(timing.current, update);
  }, []);
  const hasTerminalOutcome = useCallback(() => terminalCaptured.current, []);
  useEffect(() => {
    const onVisibilityChange = () => {
      const current = timing.current;

      if (document.visibilityState === "hidden") {
        current.hiddenSince = performance.now();
      } else if (current.hiddenSince !== null) {
        current.hiddenMs += performance.now() - current.hiddenSince;
        current.hiddenSince = null;
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);
  const captureTerminal = useCallback(
    ({
      outcome,
      failureStage,
      measurementKind = "iframe_load_double_raf",
    }: {
      outcome: ReplayOutcome;
      failureStage: string | null;
      measurementKind?: "iframe_load_double_raf" | "viewer_ready_message";
    }) => {
      if (terminalCaptured.current) return;

      terminalCaptured.current = true;
      const endedAt = performance.now();
      const current = timing.current;
      const succeeded = outcome === "success";
      const totalMs = durationMs({
        start: current.requestStartedAt,
        end: endedAt,
      });

      if (succeeded)
        setBadge({ delivery: current.delivery, firstPaintMs: totalMs });

      captureDatadogRumAction({
        name: "coworld_replay_approximate_first_paint",
        context: replayTimingContext({
          current,
          endedAt,
          outcome,
          failureStage,
          measurementKind,
          coworldId,
          telemetryContext: {
            episodeId: telemetryContext.episodeId,
            game: telemetryContext.game,
            gameVersion: telemetryContext.gameVersion,
          },
          surface,
        }),
      });
    },
    [
      coworldId,
      surface,
      telemetryContext.episodeId,
      telemetryContext.game,
      telemetryContext.gameVersion,
    ],
  );
  return {
    readTiming,
    recordTiming,
    hasTerminalOutcome,
    badge,
    setBadge,
    captureTerminal,
  };
}

export type ReplayTimingController = ReturnType<typeof useReplayTiming>;
